import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { db, storage } from "../../../firebase/config";
import { doc, onSnapshot, collection, addDoc, query, orderBy } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useAuth } from "../../../context/AuthContext";
import { useTheme } from "../../../context/ThemeContext";
import Header from "../../../components/Header";
import { registrarAccion } from "../../../utils/trazabilidad";
import { exportarHistoriaClinica } from "../../../utils/exportar";

export default function HistoriaClinica() {
  const { id } = useParams();
  const { currentUser, userRole, clinicaId, clinicaData } = useAuth();
  const { theme } = useTheme();
  const [paciente, setPaciente] = useState(null);
  const [antecedentes, setAntecedentes] = useState(null);
  const [editandoAnt, setEditandoAnt] = useState(false);
  const [datosAnt, setDatosAnt] = useState({});
  const [visitas, setVisitas] = useState([]);
  const [modalVisita, setModalVisita] = useState(false);
  const [archivosVisita, setArchivosVisita] = useState([]);
  const [expandida, setExpandida] = useState(null);
  const [nuevaVisita, setNuevaVisita] = useState({
    fecha: new Date().toISOString().split('T')[0],
    tipo: 'consulta',
    motivoVisita: '',
    // Agudeza visual sin corrección
    avODSC: '', avOISC: '',
    // Agudeza visual con corrección
    avODCC: '', avOICC: '',
    // Refracción foróptero
    refraccionForopteroOD: '', refraccionForopteroOI: '',
    // Lensómetro
    refraccionLensometroOD: '', refraccionLensometroOI: '',
    // PIO
    pioOD: '', pioOI: '',
    // Biomicroscopía
    biomOD: '', biomOI: '',
    // Fondo de ojos
    fondoOD: '', fondoOI: '',
    // Resto
    diagnostico: '', tratamiento: '', estudiosIndicados: '',
    observaciones: '', recetas: '',
  });

  const canEditHC = ['medico', 'gestion_general'].includes(userRole);
  const canExportar = ['gestion_general', 'administracion'].includes(userRole);

  const btnPrimaryStyle = { background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentDark})`, border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: '#FFFFFF', fontWeight: '600', fontFamily: "'Montserrat', sans-serif" };
  const btnSecondaryStyle = { background: 'transparent', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" };
  const btnSmallStyle = { background: 'transparent', border: `0.5px solid ${theme.borderInput}`, borderRadius: '6px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" };
  const inputStyle = { width: '100%', padding: '10px 14px', fontSize: '13px', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', outline: 'none', color: theme.primary, background: theme.bgInput, boxSizing: 'border-box', fontFamily: "'Montserrat', sans-serif" };
  const labelStyle = { fontSize: '11px', color: theme.secondary, display: 'block', marginBottom: '6px', fontWeight: '500', letterSpacing: '1px' };
  const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: theme.bgOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
  const modalStyle = { background: theme.bgModal, borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '720px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.3)', border: `0.5px solid ${theme.border}` };
  const fieldBoxStyle = { background: theme.bgCard, borderRadius: '8px', padding: '10px 14px', border: `0.5px solid ${theme.border}` };
  const fieldLabelStyle = { fontSize: '10px', color: theme.secondary, fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '4px' };
  const fieldValueStyle = { fontSize: '13px', color: theme.primary, lineHeight: '1.5' };
  const seccionStyle = { fontSize: '11px', fontWeight: '700', color: theme.accent, letterSpacing: '1.5px', textTransform: 'uppercase', margin: '16px 0 8px', gridColumn: '1 / -1', borderBottom: `0.5px solid ${theme.border}`, paddingBottom: '6px' };

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'pacientes', id), snap => {
      if (snap.exists()) setPaciente({ id: snap.id, ...snap.data() });
    });
    return unsub;
  }, [id]);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'pacientes', id, 'historia', 'antecedentes'), snap => {
      if (snap.exists()) { setAntecedentes(snap.data()); setDatosAnt(snap.data()); }
      else { setAntecedentes({}); setDatosAnt({}); }
    });
    return unsub;
  }, [id]);

  useEffect(() => {
    const q = query(collection(db, 'pacientes', id, 'historia', 'visitas', 'entradas'), orderBy('fechaRegistro', 'desc'));
    const unsub = onSnapshot(q, snap => setVisitas(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return unsub;
  }, [id]);

  async function guardarAntecedentes() {
    const { setDoc } = await import('firebase/firestore');
    await setDoc(doc(db, 'pacientes', id, 'historia', 'antecedentes'), datosAnt, { merge: true });
    await registrarAccion(currentUser?.email, userRole, 'Historia Clínica', 'Editar antecedentes', paciente?.apellido + ', ' + paciente?.nombre, clinicaId);
    setEditandoAnt(false);
  }

  async function guardarVisita() {
    if (!nuevaVisita.diagnostico && !nuevaVisita.observaciones && !nuevaVisita.motivoVisita) return;
    let archivosGuardados = [];
    for (const archivo of archivosVisita) {
      try {
        const nombreLimpio = archivo.name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9._-]/g, '');
        const storageRef = ref(storage, `historias/${id}/${Date.now()}_${nombreLimpio}`);
        await uploadBytes(storageRef, archivo);
        const url = await getDownloadURL(storageRef);
        archivosGuardados.push({ nombre: archivo.name, url, tipo: archivo.type });
      } catch (e) { console.warn('Error al subir archivo:', e); }
    }
    await addDoc(collection(db, 'pacientes', id, 'historia', 'visitas', 'entradas'), {
      ...nuevaVisita, medico: currentUser?.email, fechaRegistro: new Date(), archivos: archivosGuardados,
    });
    await registrarAccion(currentUser?.email, userRole, 'Historia Clínica', 'Nueva visita', paciente?.apellido + ', ' + paciente?.nombre, clinicaId);
    setModalVisita(false);
    setArchivosVisita([]);
    setNuevaVisita({
      fecha: new Date().toISOString().split('T')[0], tipo: 'consulta', motivoVisita: '',
      avODSC: '', avOISC: '', avODCC: '', avOICC: '',
      refraccionForopteroOD: '', refraccionForopteroOI: '',
      refraccionLensometroOD: '', refraccionLensometroOI: '',
      pioOD: '', pioOI: '', biomOD: '', biomOI: '',
      fondoOD: '', fondoOI: '',
      diagnostico: '', tratamiento: '', estudiosIndicados: '', observaciones: '', recetas: '',
    });
  }

  async function exportarHC() {
    try { await exportarHistoriaClinica(paciente, antecedentes, visitas, clinicaData); }
    catch (e) { alert('Error al exportar: ' + e.message); }
  }

  const campo = (label, value) => value ? (
    <div style={fieldBoxStyle}>
      <div style={fieldLabelStyle}>{label}</div>
      <div style={fieldValueStyle}>{value}</div>
    </div>
  ) : null;

  if (!paciente) return <div style={{ minHeight: '100vh', background: theme.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" }}>Cargando...</div>;

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: "'Montserrat', sans-serif" }}>
      <Header titulo="Historia Clínica" backLabel={`${paciente?.apellido}, ${paciente?.nombre}`} backRuta={`/pacientes/${id}`} />
      <div style={{ padding: '12px 24px 0', display: 'flex', justifyContent: 'flex-end', gap: '8px', background: theme.bg }}>
        {canExportar && <button onClick={exportarHC} style={btnSecondaryStyle}>Exportar HC</button>}
        {canEditHC && <button onClick={() => setModalVisita(true)} style={btnPrimaryStyle}>+ Nueva visita</button>}
      </div>

      <div style={{ padding: '24px', background: theme.bg }}>
        {paciente.alergia && (
          <div style={{ background: theme.errorBg, border: `0.5px solid ${theme.error}`, borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', color: theme.error, fontWeight: '500' }}>
            ⚠ ALERGIA: {paciente.detalleAlergia}
          </div>
        )}

        {/* ANTECEDENTES */}
        <div style={{ background: theme.bgCard, borderRadius: '12px', border: `0.5px solid ${theme.border}`, padding: '20px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: theme.secondary, letterSpacing: '1.5px', textTransform: 'uppercase' }}>Antecedentes</div>
            {canEditHC && !editandoAnt && <button onClick={() => setEditandoAnt(true)} style={btnSmallStyle}>Editar</button>}
            {editandoAnt && <div style={{ display: 'flex', gap: '6px' }}>
              <button onClick={guardarAntecedentes} style={btnPrimaryStyle}>Guardar</button>
              <button onClick={() => { setEditandoAnt(false); setDatosAnt(antecedentes); }} style={btnSecondaryStyle}>Cancelar</button>
            </div>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
            {[
              { label: 'Antecedentes generales', key: 'antecedentesGenerales' },
              { label: 'Antecedentes oftalmológicos', key: 'antecedentesOftalmologicos' },
              { label: 'Medicación habitual', key: 'medicacionHabitual' },
            ].map(c => (
              <div key={c.key} style={fieldBoxStyle}>
                <div style={fieldLabelStyle}>{c.label}</div>
                {editandoAnt
                  ? <textarea value={datosAnt[c.key] || ''} onChange={e => setDatosAnt({...datosAnt, [c.key]: e.target.value})} rows={2} style={{ ...inputStyle, resize: 'vertical', padding: '6px 8px' }} />
                  : <div style={fieldValueStyle}>{antecedentes?.[c.key] || '—'}</div>}
              </div>
            ))}
            <div style={fieldBoxStyle}>
              <div style={fieldLabelStyle}>Alergia</div>
              {editandoAnt ? (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <input type="checkbox" checked={datosAnt.alergia || false} onChange={e => setDatosAnt({...datosAnt, alergia: e.target.checked})} />
                    <span style={{ fontSize: '12px', color: theme.secondary }}>Presenta alergia</span>
                  </div>
                  {datosAnt.alergia && <input value={datosAnt.detalleAlergia || ''} onChange={e => setDatosAnt({...datosAnt, detalleAlergia: e.target.value})} placeholder="Detalle..." style={{ ...inputStyle, padding: '6px 8px' }} />}
                </div>
              ) : <div style={fieldValueStyle}>{antecedentes?.alergia ? antecedentes.detalleAlergia : 'No refiere'}</div>}
            </div>
          </div>
        </div>

        {/* VISITAS */}
        <div style={{ fontSize: '13px', fontWeight: '700', color: theme.secondary, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '12px' }}>
          Registro de visitas ({visitas.length})
        </div>

        {visitas.length === 0
          ? <div style={{ textAlign: 'center', color: theme.secondary, padding: '32px', fontSize: '13px', background: theme.bgCard, borderRadius: '10px', border: `0.5px solid ${theme.border}` }}>No hay visitas registradas</div>
          : visitas.map(v => {
              const fecha = v.fechaRegistro?.toDate ? v.fechaRegistro.toDate().toLocaleDateString('es-AR') : '—';
              const tipoColors = { consulta: theme.infoBg, control: theme.successBg, estudio: theme.warningBg, cirugia: theme.errorBg };
              const tipoTextColors = { consulta: theme.accent, control: theme.success, estudio: theme.warning, cirugia: theme.error };
              return (
                <div key={v.id} style={{ background: theme.bgCard, borderRadius: '12px', border: `0.5px solid ${theme.border}`, marginBottom: '10px', overflow: 'hidden' }}>
                  <div onClick={() => setExpandida(expandida === v.id ? null : v.id)} style={{ padding: '14px 18px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: theme.primary }}>{fecha}</div>
                      <div style={{ fontSize: '12px', color: theme.secondary }}>{v.motivoVisita || v.medico}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '99px', background: tipoColors[v.tipo] || theme.bgCard, color: tipoTextColors[v.tipo] || theme.secondary, fontWeight: '500' }}>
                        {v.tipo?.charAt(0).toUpperCase() + v.tipo?.slice(1)}
                      </span>
                      <span style={{ color: theme.secondary, fontSize: '16px' }}>{expandida === v.id ? '▲' : '▼'}</span>
                    </div>
                  </div>
                  {expandida === v.id && (
                    <div style={{ padding: '0 18px 16px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px' }}>
                        {v.motivoVisita && <div style={{ ...fieldBoxStyle, gridColumn: '1 / -1' }}><div style={fieldLabelStyle}>Motivo de visita</div><div style={fieldValueStyle}>{v.motivoVisita}</div></div>}

                        {(v.avODSC || v.avOISC || v.avODCC || v.avOICC) && <>
                          <div style={seccionStyle}>Agudeza visual</div>
                          {campo('AV OD sin corrección', v.avODSC)}
                          {campo('AV OI sin corrección', v.avOISC)}
                          {campo('AV OD con corrección', v.avODCC)}
                          {campo('AV OI con corrección', v.avOICC)}
                        </>}

                        {(v.refraccionForopteroOD || v.refraccionForopteroOI) && <>
                          <div style={seccionStyle}>Refracción foróptero</div>
                          {campo('OD', v.refraccionForopteroOD)}
                          {campo('OI', v.refraccionForopteroOI)}
                        </>}

                        {(v.refraccionLensometroOD || v.refraccionLensometroOI) && <>
                          <div style={seccionStyle}>Lensómetro</div>
                          {campo('OD', v.refraccionLensometroOD)}
                          {campo('OI', v.refraccionLensometroOI)}
                        </>}

                        {(v.pioOD || v.pioOI) && <>
                          <div style={seccionStyle}>Presión intraocular</div>
                          {campo('PIO OD (mmHg)', v.pioOD)}
                          {campo('PIO OI (mmHg)', v.pioOI)}
                        </>}

                        {(v.biomOD || v.biomOI) && <>
                          <div style={seccionStyle}>Biomicroscopía</div>
                          {v.biomOD && <div style={{ ...fieldBoxStyle, gridColumn: 'span 2' }}><div style={fieldLabelStyle}>OD</div><div style={fieldValueStyle}>{v.biomOD}</div></div>}
                          {v.biomOI && <div style={{ ...fieldBoxStyle, gridColumn: 'span 2' }}><div style={fieldLabelStyle}>OI</div><div style={fieldValueStyle}>{v.biomOI}</div></div>}
                        </>}

                        {(v.fondoOD || v.fondoOI) && <>
                          <div style={seccionStyle}>Fondo de ojos</div>
                          {v.fondoOD && <div style={{ ...fieldBoxStyle, gridColumn: 'span 2' }}><div style={fieldLabelStyle}>OD</div><div style={fieldValueStyle}>{v.fondoOD}</div></div>}
                          {v.fondoOI && <div style={{ ...fieldBoxStyle, gridColumn: 'span 2' }}><div style={fieldLabelStyle}>OI</div><div style={fieldValueStyle}>{v.fondoOI}</div></div>}
                        </>}

                        {(v.diagnostico || v.tratamiento || v.estudiosIndicados || v.observaciones || v.recetas) && <>
                          <div style={seccionStyle}>Diagnóstico y tratamiento</div>
                          {v.diagnostico && <div style={{ ...fieldBoxStyle, gridColumn: '1 / -1' }}><div style={fieldLabelStyle}>Diagnóstico</div><div style={fieldValueStyle}>{v.diagnostico}</div></div>}
                          {v.tratamiento && <div style={{ ...fieldBoxStyle, gridColumn: '1 / -1' }}><div style={fieldLabelStyle}>Tratamiento</div><div style={fieldValueStyle}>{v.tratamiento}</div></div>}
                          {v.estudiosIndicados && <div style={{ ...fieldBoxStyle, gridColumn: '1 / -1' }}><div style={fieldLabelStyle}>Estudios indicados</div><div style={fieldValueStyle}>{v.estudiosIndicados}</div></div>}
                          {v.observaciones && <div style={{ ...fieldBoxStyle, gridColumn: '1 / -1' }}><div style={fieldLabelStyle}>Observaciones</div><div style={fieldValueStyle}>{v.observaciones}</div></div>}
                          {v.recetas && <div style={{ ...fieldBoxStyle, gridColumn: '1 / -1' }}><div style={fieldLabelStyle}>Recetas</div><div style={fieldValueStyle}>{v.recetas}</div></div>}
                        </>}

                        {v.archivos && v.archivos.length > 0 && (
                          <div style={{ ...fieldBoxStyle, gridColumn: '1 / -1' }}>
                            <div style={fieldLabelStyle}>Archivos adjuntos</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                              {v.archivos.map((a, i) => (
                                <a key={i} href={a.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '6px', background: theme.infoBg, color: theme.accent, textDecoration: 'none', fontWeight: '500' }}>📎 {a.nombre}</a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
        }
      </div>

      {/* MODAL NUEVA VISITA */}
      {modalVisita && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '16px', fontWeight: '600', color: theme.primary }}>Nueva visita</span>
              <button onClick={() => setModalVisita(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: theme.secondary }}>×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>

              <div><label style={labelStyle}>FECHA</label><input type="date" value={nuevaVisita.fecha} onChange={e => setNuevaVisita({...nuevaVisita, fecha: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>TIPO</label>
                <select value={nuevaVisita.tipo} onChange={e => setNuevaVisita({...nuevaVisita, tipo: e.target.value})} style={inputStyle}>
                  <option value="consulta">Consulta</option>
                  <option value="control">Control</option>
                  <option value="estudio">Estudio</option>
                  <option value="cirugia">Cirugía</option>
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>MOTIVO DE VISITA</label><input value={nuevaVisita.motivoVisita} onChange={e => setNuevaVisita({...nuevaVisita, motivoVisita: e.target.value})} style={inputStyle} /></div>

              <div style={{ ...{}, gridColumn: '1 / -1', fontSize: '11px', fontWeight: '700', color: theme.accent, letterSpacing: '1.5px', textTransform: 'uppercase', borderBottom: `0.5px solid ${theme.border}`, paddingBottom: '6px', marginTop: '4px' }}>Agudeza visual</div>
              <div><label style={labelStyle}>AV OD SIN CORRECCIÓN</label><input value={nuevaVisita.avODSC} onChange={e => setNuevaVisita({...nuevaVisita, avODSC: e.target.value})} placeholder="ej: 20/200" style={inputStyle} /></div>
              <div><label style={labelStyle}>AV OI SIN CORRECCIÓN</label><input value={nuevaVisita.avOISC} onChange={e => setNuevaVisita({...nuevaVisita, avOISC: e.target.value})} placeholder="ej: 20/200" style={inputStyle} /></div>
              <div><label style={labelStyle}>AV OD CON CORRECCIÓN</label><input value={nuevaVisita.avODCC} onChange={e => setNuevaVisita({...nuevaVisita, avODCC: e.target.value})} placeholder="ej: 20/20" style={inputStyle} /></div>
              <div><label style={labelStyle}>AV OI CON CORRECCIÓN</label><input value={nuevaVisita.avOICC} onChange={e => setNuevaVisita({...nuevaVisita, avOICC: e.target.value})} placeholder="ej: 20/20" style={inputStyle} /></div>

              <div style={{ gridColumn: '1 / -1', fontSize: '11px', fontWeight: '700', color: theme.accent, letterSpacing: '1.5px', textTransform: 'uppercase', borderBottom: `0.5px solid ${theme.border}`, paddingBottom: '6px', marginTop: '4px' }}>Refracción</div>
              <div><label style={labelStyle}>FORÓPTERO OD</label><input value={nuevaVisita.refraccionForopteroOD} onChange={e => setNuevaVisita({...nuevaVisita, refraccionForopteroOD: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>FORÓPTERO OI</label><input value={nuevaVisita.refraccionForopteroOI} onChange={e => setNuevaVisita({...nuevaVisita, refraccionForopteroOI: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>LENSÓMETRO OD</label><input value={nuevaVisita.refraccionLensometroOD} onChange={e => setNuevaVisita({...nuevaVisita, refraccionLensometroOD: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>LENSÓMETRO OI</label><input value={nuevaVisita.refraccionLensometroOI} onChange={e => setNuevaVisita({...nuevaVisita, refraccionLensometroOI: e.target.value})} style={inputStyle} /></div>

              <div style={{ gridColumn: '1 / -1', fontSize: '11px', fontWeight: '700', color: theme.accent, letterSpacing: '1.5px', textTransform: 'uppercase', borderBottom: `0.5px solid ${theme.border}`, paddingBottom: '6px', marginTop: '4px' }}>Presión intraocular</div>
              <div><label style={labelStyle}>PIO OD (mmHg)</label><input type="number" value={nuevaVisita.pioOD} onChange={e => setNuevaVisita({...nuevaVisita, pioOD: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>PIO OI (mmHg)</label><input type="number" value={nuevaVisita.pioOI} onChange={e => setNuevaVisita({...nuevaVisita, pioOI: e.target.value})} style={inputStyle} /></div>

              <div style={{ gridColumn: '1 / -1', fontSize: '11px', fontWeight: '700', color: theme.accent, letterSpacing: '1.5px', textTransform: 'uppercase', borderBottom: `0.5px solid ${theme.border}`, paddingBottom: '6px', marginTop: '4px' }}>Biomicroscopía</div>
              <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>BIOMICROSCOPÍA OD</label><textarea value={nuevaVisita.biomOD} onChange={e => setNuevaVisita({...nuevaVisita, biomOD: e.target.value})} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>BIOMICROSCOPÍA OI</label><textarea value={nuevaVisita.biomOI} onChange={e => setNuevaVisita({...nuevaVisita, biomOI: e.target.value})} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>

              <div style={{ gridColumn: '1 / -1', fontSize: '11px', fontWeight: '700', color: theme.accent, letterSpacing: '1.5px', textTransform: 'uppercase', borderBottom: `0.5px solid ${theme.border}`, paddingBottom: '6px', marginTop: '4px' }}>Fondo de ojos</div>
              <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>FONDO OD</label><textarea value={nuevaVisita.fondoOD} onChange={e => setNuevaVisita({...nuevaVisita, fondoOD: e.target.value})} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>FONDO OI</label><textarea value={nuevaVisita.fondoOI} onChange={e => setNuevaVisita({...nuevaVisita, fondoOI: e.target.value})} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>

              <div style={{ gridColumn: '1 / -1', fontSize: '11px', fontWeight: '700', color: theme.accent, letterSpacing: '1.5px', textTransform: 'uppercase', borderBottom: `0.5px solid ${theme.border}`, paddingBottom: '6px', marginTop: '4px' }}>Diagnóstico y tratamiento</div>
              <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>DIAGNÓSTICO</label><textarea value={nuevaVisita.diagnostico} onChange={e => setNuevaVisita({...nuevaVisita, diagnostico: e.target.value})} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>TRATAMIENTO</label><textarea value={nuevaVisita.tratamiento} onChange={e => setNuevaVisita({...nuevaVisita, tratamiento: e.target.value})} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>ESTUDIOS INDICADOS</label><input value={nuevaVisita.estudiosIndicados} onChange={e => setNuevaVisita({...nuevaVisita, estudiosIndicados: e.target.value})} style={inputStyle} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>OBSERVACIONES</label><textarea value={nuevaVisita.observaciones} onChange={e => setNuevaVisita({...nuevaVisita, observaciones: e.target.value})} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>RECETAS</label><textarea value={nuevaVisita.recetas} onChange={e => setNuevaVisita({...nuevaVisita, recetas: e.target.value})} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>ARCHIVOS ADJUNTOS (JPG, PDF)</label>
                <input type="file" multiple accept=".jpg,.jpeg,.pdf,.png" onChange={e => setArchivosVisita(Array.from(e.target.files))} style={{ fontSize: '13px', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" }} />
                {archivosVisita.length > 0 && (
                  <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {archivosVisita.map((f, i) => <span key={i} style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '99px', background: theme.infoBg, color: theme.accent, fontWeight: '500' }}>{f.name}</span>)}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
              <button onClick={guardarVisita} style={btnPrimaryStyle}>Guardar visita</button>
              <button onClick={() => setModalVisita(false)} style={btnSecondaryStyle}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}